import { Component, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { ISelectOption, ITimeGroup } from 'app/models/filter-form.models';
import { FilterService } from 'app/services/filter.service';
import { convertTimeNumberToString, getDayNight } from 'app/utils/date.utils';
import { makeControlDirty } from 'app/utils/form.utils';
import { DayNight } from '../../../../../models/filter.models';
import { FilterItemComponent } from '../../filter-item/filter-item.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { NgFor } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';

const emptyLabel = 'Any';

const formatTime = (hour?: string, min?: string): string => (hour && min ? `${hour}:${min}` : emptyLabel);

@Component({
  selector: 'app-time-filter',
  templateUrl: './time-filter.component.html',
  styleUrls: ['./time-filter.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [
    FormsModule,
    FilterItemComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    MatButtonToggleGroup,
    MatButtonToggle,
  ],
})
export class TimeFilterComponent implements OnInit {
  @ViewChild('time') ngModelGroup!: NgModelGroup;

  selectedDayNight: DayNight | undefined;

  private dayTimeGroup: ITimeGroup = {
    hoursFrom: '06',
    minutesFrom: '00',
    hoursTo: '18',
    minutesTo: '00',
  };

  private nightTimeGroup: ITimeGroup = {
    hoursFrom: '18',
    minutesFrom: '00',
    hoursTo: '06',
    minutesTo: '00',
  };

  timeGroup: ITimeGroup = {
    hoursFrom: undefined,
    minutesFrom: undefined,
    hoursTo: undefined,
    minutesTo: undefined,
  };

  minutes = Array.from(Array(60).keys()).map((min) => convertTimeNumberToString(min));

  hours = Array.from(Array(24).keys()).map((hr) => convertTimeNumberToString(hr));

  dayNightOptions: ISelectOption[] = [
    { value: DayNight.DAY, viewValue: 'Day time' },
    { value: DayNight.NIGHT, viewValue: 'Night time' },
  ];

  constructor(private filterService: FilterService) {}

  ngOnInit(): void {
    const filters = this.filterService.appliedFilters.dateFilter;

    if (!filters) return;

    let hoursFrom;
    let hoursTo;
    let minutesFrom;
    let minutesTo;

    if (filters.fromTime) {
      [hoursFrom, minutesFrom] = filters.fromTime.split(':');
    }

    if (filters.toTime) {
      [hoursTo, minutesTo] = filters.toTime.split(':');
    }

    this.timeGroup = {
      hoursFrom,
      minutesFrom,
      hoursTo,
      minutesTo,
    };

    this.onTimeChange();
  }

  get timeRange() {
    const { hoursFrom, minutesFrom, hoursTo, minutesTo } = this.timeGroup;

    const timeFrom = formatTime(hoursFrom, minutesFrom);
    const timeTo = formatTime(hoursTo, minutesTo);

    return timeFrom !== emptyLabel || timeTo !== emptyLabel ? `${timeFrom} - ${timeTo}` : undefined;
  }

  onFromHourChange() {
    if (!this.timeGroup.minutesFrom) this.timeGroup.minutesFrom = '00';

    this.onTimeChange();
  }

  onFromMinuteChange() {
    if (!this.timeGroup.hoursFrom && this.timeGroup.minutesFrom) this.timeGroup.hoursFrom = '00';

    this.onTimeChange();
  }

  onToHourChange(value: string) {
    if (this.timeGroup.minutesFrom && this.timeGroup.hoursFrom === value) {
      this.timeGroup.minutesTo = convertTimeNumberToString(+this.timeGroup.minutesFrom + 1);

      return;
    }

    this.timeGroup.minutesTo = '00';

    this.onTimeChange();
  }

  onToMinuteChange() {
    if (!this.timeGroup.hoursTo && this.timeGroup.minutesTo) this.timeGroup.hoursTo = '00';

    this.onTimeChange();
  }

  onDayNightChange() {
    if (this.selectedDayNight === DayNight.DAY) {
      this.timeGroup = this.dayTimeGroup;
    } else if (this.selectedDayNight === DayNight.NIGHT) {
      this.timeGroup = this.nightTimeGroup;
    }
  }

  clearFilter = () => {
    this.timeGroup.hoursFrom = undefined;
    this.timeGroup.minutesFrom = undefined;
    this.timeGroup.hoursTo = undefined;
    this.timeGroup.minutesTo = undefined;
    this.selectedDayNight = undefined;

    makeControlDirty(this.ngModelGroup.control);
  };

  private onTimeChange() {
    this.selectedDayNight = getDayNight(
      this.timeGroup.hoursFrom,
      this.timeGroup.minutesFrom,
      this.timeGroup.hoursTo,
      this.timeGroup.minutesTo,
    );
  }
}
